import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as signalR from '@microsoft/signalr';
import { fetchCustomerById } from '../api/customersApi';

const HUB_URL = `${import.meta.env.VITE_API_URL}/hub/liverefresh`;

// Dirty-guarded fetch of a single customer. The ViewCustomer page's Details
// card edits `['customer', id]` locally and auto-saves on debounce — we
// CANNOT blindly invalidate the query while the user is mid-edit or we'll
// clobber their in-flight keystrokes when the server echo comes back.
//
// `isDirtyRef` is a ref the card owns and flips to `true` while there are
// unsaved local changes. While dirty, SignalR invalidations are stashed in
// `pendingRefreshRef` and flushed via the returned `flushPendingRefresh()`
// once the card calls it (typically from the save mutation's onSuccess).
export default function useCustomer(id, isDirtyRef) {
    const queryClient = useQueryClient();
    const pendingRefreshRef = useRef(false);

    const query = useQuery({
        queryKey: ['customer', id],
        queryFn: () => fetchCustomerById(id),
        enabled: id != null,
        // 404s on a wrong-tenant / missing id shouldn't retry — retrying just
        // delays the "not found" panel. Genuine 5xx will surface to the user.
        retry: false,
    });

    useEffect(() => {
        if (id == null) return undefined;

        const connection = new signalR.HubConnectionBuilder()
            .withUrl(HUB_URL, {
                accessTokenFactory: () =>
                    window.Clerk.session.getToken({ template: 'marginpal-api' }),
            })
            .withAutomaticReconnect()
            .build();

        connection.on('UpdateCustomerTable', () => {
            if (isDirtyRef?.current) {
                // Stash: we'll invalidate once the user's save settles.
                pendingRefreshRef.current = true;
                return;
            }
            queryClient.invalidateQueries({ queryKey: ['customer', id] });
        });

        connection.start().catch(err => console.error('SignalR connection error:', err));

        return () => {
            connection.stop();
        };
    }, [queryClient, id, isDirtyRef]);

    const flushPendingRefresh = () => {
        if (pendingRefreshRef.current) {
            pendingRefreshRef.current = false;
            queryClient.invalidateQueries({ queryKey: ['customer', id] });
        }
    };

    return { ...query, flushPendingRefresh };
}
